<template>
  <div class="table-filter-option">
    <ul class="pagination">
      <li>
        <button
          type="button"
          @click="onClickPreviousPage"
          :disabled="isOnFirstPage"
          aria-label="Previous page"
        >
          <IconCaretLeft />
        </button>
      </li>

      <li v-for="page in pages" :key="page.name">
        <button
          type="button"
          :disabled="page.isDisabled"
          :class="{ active: currentPage === page.name }"
          @click="onClickPage(page.name)"
        >
          {{ page.name }}
        </button>
      </li>

      <li>
        <button
          type="button"
          @click="onClickNextPage"
          :disabled="isOnLastPage"
          aria-label="Next page"
        >
          <IconCaretRight />
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import IconCaretLeft from '@/icons/arrows/IconCaretLeft.svg?component';
import IconCaretRight from '@/icons/arrows/IconCaretRight.svg?component';

const maxVisibleButtons = 3;

interface Props {
  totalPages: number;
  currentPage: number;
}

interface Page {
  name: number;
  isDisabled: boolean;
}

const emit = defineEmits(['pageChanged']);
const props = defineProps<Props>();

const pages = computed(() => {
  const range = new Array<Page>();

  const startPage =
    props.currentPage === 1
      ? 1
      : props.currentPage === props.totalPages
        ? props.totalPages - 2
        : props.currentPage === props.totalPages
          ? props.totalPages - maxVisibleButtons
          : props.currentPage - 1;

  for (let i = startPage; i <= Math.min(startPage + maxVisibleButtons - 1, props.totalPages); i++) {
    if (i > 0) {
      range.push({
        name: i,
        isDisabled: i === props.currentPage,
      });
    }
  }

  return range;
});

const isOnFirstPage = computed(() => {
  return props.currentPage === 1;
});

const isOnLastPage = computed(() => {
  return props.currentPage === props.totalPages;
});

const onClickPreviousPage = () => {
  emit('pageChanged', props.currentPage - 1);
};
const onClickPage = (page: number) => {
  emit('pageChanged', page);
};
const onClickNextPage = () => {
  emit('pageChanged', props.currentPage + 1);
};
</script>

<style lang="scss">
.pagination {
  @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxxs);
  list-style-type: none;

  li {
    @include mixins.flex-center($wrap: true);
  }

  button {
    @include mixins.flex-center($wrap: true);
    @include sizing.padding('xxxs');
    appearance: none;
    background: transparent;
    border: 0;
    color: variables.$vantage-base-primitives-color-tertiary-500;
    cursor: pointer;
    min-width: variables.$vantage-base-sizing-lg;
  }

  :disabled {
    color: variables.$vantage-base-primitives-color-tertiary-200;
    pointer-events: none;
  }

  .active {
    background: variables.$vantage-base-primitives-color-border-100;
    border-radius: variables.$vantage-base-sizing-xxxs;
    color: variables.$vantage-base-primitives-color-tertiary-500;
  }
}
</style>
